import AppHeader from '../components/Header/AppHeader';
import OfflineBanner from '../components/OfflineBanner/OfflineBanner';
import { ScreenSizeContextProvider } from '../context/ScreenSizeContext';
import AuthContextProvider from '../context/AuthContext';
import Main from '../pages/Main';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { ToastProvider } from '@ordr-as/umami-react/toast';
import { OverlayProvider } from '@ordr-as/umami-react/modal';
import { LanguageProvider } from '../context/LanguageContext';
import ScrollToTop from '../components/ScrollToTop';
import ErrorBoundary from '../containers/ErrorBoundary';
import { PinScreenSettingsContextProvider } from '../context/PinScreenSettingsContext';
import { ApiFetcherProvider } from '../context/ApiFetcherProvider';
import { MenuBuilderFetcherProvider } from '../context/MenuBuilderFetcherProvider';
import { persistQueryClient } from '@tanstack/react-query-persist-client';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import { isAdyenDevice } from '../utils/isAdyenDevice';
import { VersionNotifier } from '../components/VersionNotifier';

const isLowEndDevice = isAdyenDevice();
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: !isLowEndDevice,
      refetchOnMount: !isLowEndDevice,
      gcTime: isLowEndDevice ? 30_000 : 5 * 60_000, // 30 seconds for low-end devices, 5 minutes for others
    },
  },
});

if (!isLowEndDevice) {
  const persister = createSyncStoragePersister({
    storage: window.localStorage,
    serialize: (client) => {
      return JSON.stringify(client);
    },
  });

  persistQueryClient({
    queryClient,
    persister,
    dehydrateOptions: {
      shouldDehydrateQuery: (query) => {
        return !!query.options.meta?.persist;
      },
    },
    maxAge: 1000 * 60 * 60 * 24, // Keep cache for 24 hours even after refresh
  });
}

const PrivateRoute = () => {
  return (
    <ScreenSizeContextProvider>
      <LanguageProvider>
        <ToastProvider>
          <VersionNotifier />
          <OverlayProvider>
            <AuthContextProvider>
              <QueryClientProvider client={queryClient}>
                <ApiFetcherProvider>
                  <MenuBuilderFetcherProvider>
                    <PinScreenSettingsContextProvider>
                      <ScrollToTop />
                      <div className="h-screen flex flex-col overflow-hidden">
                        <OfflineBanner />
                        <AppHeader />
                        <div className="flex-1 overflow-auto">
                          <ErrorBoundary>
                            <Main />
                          </ErrorBoundary>
                        </div>
                      </div>
                    </PinScreenSettingsContextProvider>
                    <ReactQueryDevtools initialIsOpen={false} />
                  </MenuBuilderFetcherProvider>
                </ApiFetcherProvider>
              </QueryClientProvider>
            </AuthContextProvider>
          </OverlayProvider>
        </ToastProvider>
      </LanguageProvider>
    </ScreenSizeContextProvider>
  );
};

export default PrivateRoute;
